import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogoMark, Wordmark } from "./Logo";

const NAV_LABELS = {
  en: ["ABOUT", "REPRESENTATION", "APPROACH", "CONTACT"],
  tr: ["ABOUT", "REPRESENTATION", "APPROACH", "CONTACT"],
  it: ["CHI SIAMO", "RAPPRESENTANZA", "APPROCCIO", "CONTATTI"],
};

const LINKS = [
  { href: "#about", testid: "nav-link-about" },
  { href: "#representation", testid: "nav-link-representation" },
  { href: "#approach", testid: "nav-link-approach" },
  { href: "#contact", testid: "nav-link-contact" },
];

const LANGS = [
  { code: "en", to: "/" },
  { code: "tr", to: "/tr" },
  { code: "it", to: "/it" },
];

const scrollTo = (href) => (e) => {
  e.preventDefault();
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LangSwitch = ({ lang }) => (
  <div
    className="flex items-center gap-2 font-sans text-[11px] font-medium tracking-[0.25em]"
    data-testid="language-selector"
  >
    {LANGS.map((l, i) => (
      <span key={l.code} className="flex items-center gap-2">
        {i > 0 && <span className="text-dealer-subtle/50">/</span>}
        <Link
          to={l.to}
          data-testid={`lang-link-${l.code}`}
          className={`transition-colors duration-300 ${
            lang === l.code
              ? "text-dealer-ivory"
              : "text-dealer-subtle hover:text-dealer-silver"
          }`}
        >
          {l.code.toUpperCase()}
        </Link>
      </span>
    ))}
  </div>
);

export const Nav = ({ lang = "en" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const labels = NAV_LABELS[lang] || NAV_LABELS.en;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-dealer-black/95 border-b border-dealer-ivory/10"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 sm:px-10">
          <a
            href="#top"
            onClick={scrollTo("#top")}
            className="flex items-center gap-3"
            data-testid="navbar-brand-logo"
            aria-label="DEALER — home"
          >
            <LogoMark size={30} />
            <Wordmark className="text-lg" />
          </a>

          <div className="hidden items-center gap-10 md:flex">
            <nav className="flex items-center gap-10" aria-label="Primary">
              {LINKS.map((l, i) => (
                <a
                  key={l.testid}
                  href={l.href}
                  onClick={scrollTo(l.href)}
                  data-testid={l.testid}
                  className="group relative font-sans text-[11px] font-medium tracking-[0.25em] text-dealer-silver transition-colors duration-300 hover:text-dealer-ivory"
                >
                  {labels[i]}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-dealer-burgundybright transition-[width] duration-500 group-hover:w-full" />
                </a>
              ))}
            </nav>
            <LangSwitch lang={lang} />
          </div>

          <div className="flex items-center gap-6 md:hidden">
            <LangSwitch lang={lang} />
            <button
              className="flex h-10 w-10 flex-col items-center justify-center gap-[6px]"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open-button"
              aria-label="Open menu"
            >
              <span className="h-px w-6 bg-dealer-ivory" />
              <span className="h-px w-6 bg-dealer-ivory" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-dealer-black"
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <div className="flex items-center gap-3">
                <LogoMark size={30} />
                <Wordmark className="text-lg" />
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center"
                data-testid="nav-menu-close-button"
                aria-label="Close menu"
              >
                <span className="relative block h-6 w-6">
                  <span className="absolute left-0 top-1/2 h-px w-6 rotate-45 bg-dealer-ivory" />
                  <span className="absolute left-0 top-1/2 h-px w-6 -rotate-45 bg-dealer-ivory" />
                </span>
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.testid}
                  href={l.href}
                  data-testid={`${l.testid}-mobile`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  onClick={(e) => {
                    setOpen(false);
                    scrollTo(l.href)(e);
                  }}
                  className="border-b border-dealer-ivory/10 py-5 font-serif text-3xl text-dealer-ivory"
                >
                  {labels[i]}
                </motion.a>
              ))}
            </nav>
            <p className="px-8 pb-10 dealer-eyebrow">WE ARE THE DEAL.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
